import React, { useMemo } from 'react';

import { Group, useMantineTheme } from '@mantine/core';
import { IconCheck, IconCircleOff, IconEdit, IconMail, IconSend, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import useAthleteWodupStatus from 'hooks/athlete/useAthleteWodupStatus';
import useWodupCreate from 'hooks/athlete/useWodupCreate';
import usePaymentMethods from 'hooks/paymentMethod/usePaymentMethods';
import useProfileUpdateLoginEnabled from 'hooks/profile/useProfileUpdateLoginEnabled';
import { Athlete, AthleteList, AthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

type BaseAthleteMenuProps = {
  target?: JSX.Element;
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
  handleSendLoginSetupEmail?: (athlete: AthleteRetrieve) => void;
  handleRequestPaymentInfo?: (athlete: AthleteRetrieve) => void;
  handleCreateLinkedProfile: (primaryAthleteId: string) => void;
  handleCreateDependentProfile: (primaryAthleteId: string) => void;
  handleNotificationsSettings?: () => void;
  disabled?: boolean;
  wodupEnabled?: boolean;
};

type AthleteListProps = BaseAthleteMenuProps & {
  athleteList: AthleteList;
  athleteRetrieve?: never;
};

type AthleteRetrieveProps = BaseAthleteMenuProps & {
  athleteList?: never;
  athleteRetrieve: AthleteRetrieve;
};

type AthleteActionsMenuProps = AthleteListProps | AthleteRetrieveProps;

const AthleteActionsMenu = ({
  athleteList,
  athleteRetrieve,
  target,
  handleEdit,
  handleDelete,
  handleSendLoginSetupEmail,
  handleRequestPaymentInfo,
  handleCreateLinkedProfile,
  handleCreateDependentProfile,
  handleNotificationsSettings,
  disabled = false,
  wodupEnabled = false,
}: AthleteActionsMenuProps): JSX.Element => {
  const theme = useMantineTheme();

  const { successNotification, errorNotification } = notificationHandler();

  const subscribeToWodup = useWodupCreate();
  const changeWodupStatus = useAthleteWodupStatus();
  const profileUpdateLoginMutation = useProfileUpdateLoginEnabled();

  // Reuses the payment methods already loaded for the athlete profile (same
  // query key as the General Info tab, so usually a cache hit). Skipped for
  // sub-accounts, whose "Request payment info" item is hidden anyway.
  const { data: paymentMethodsData } = usePaymentMethods({
    athleteId: athleteRetrieve?.athleteId,
    enabled: !!athleteRetrieve && !athleteRetrieve.primaryAccount,
  });
  const athleteHasPaymentMethod = (paymentMethodsData?.pages?.[0]?.data?.count ?? 0) > 0;

  const handleActivateAthlete = (athleteParam: AthleteRetrieve | Athlete | AthleteList): void => {
    subscribeToWodup.mutate(athleteParam.athleteId);
  };

  const handleReactivateAthlete = (athleteParam: AthleteRetrieve | Athlete | AthleteList): void => {
    changeWodupStatus.mutate({ athleteId: athleteParam.athleteId, activate: true });
  };

  const handleExpireAthlete = (athleteParam: AthleteRetrieve | Athlete | AthleteList): void => {
    changeWodupStatus.mutate({ athleteId: athleteParam.athleteId, activate: false });
  };

  const handleToggleLogin = (): void => {
    if (athleteRetrieve) {
      const payload = {
        loginEnabled: !athleteRetrieve.loginEnabled,
        profileId: athleteRetrieve.profile,
      };

      const actionResultMessage = payload.loginEnabled ? 'enabled' : 'disabled';

      profileUpdateLoginMutation.mutate(payload, {
        onSuccess: () => {
          successNotification(`Athlete login ${actionResultMessage} successfully`);
        },
        onError: error => {
          errorNotification('Login toggle has failed: ', error.response?.data.message);
        },
      });
    }
  };

  const menuItems = useMemo(
    () => {
      const athlete = athleteRetrieve || athleteList;

      const activateWodupItem =
        wodupEnabled && !athlete.wodupId && !athlete.activeOnWodup && handleActivateAthlete
          ? [
              {
                title: 'Activate Wodup',
                icon: <IconCheck size={theme.fontSizes.sm} />,
                onClick: () => handleActivateAthlete(athlete),
              },
            ]
          : [];

      const reactivateWodupItem =
        wodupEnabled && athlete.wodupId && !athlete.activeOnWodup && handleReactivateAthlete
          ? [
              {
                title: 'Reactivate Wodup',
                icon: <IconCheck size={theme.fontSizes.sm} />,
                onClick: () => handleReactivateAthlete(athlete),
              },
            ]
          : [];

      const expireWodupItem =
        wodupEnabled && athlete.wodupId && athlete.activeOnWodup && handleExpireAthlete
          ? [
              {
                title: 'Deactivate Wodup',
                icon: <IconCircleOff size={theme.fontSizes.sm} />,
                onClick: () => handleExpireAthlete(athlete),
              },
            ]
          : [];

      const addLinkedSubAccountItem = !athlete.primaryAccount
        ? [
            {
              title: 'Add Linked Athlete',
              icon: <IconEdit size={theme.fontSizes.sm} data-cy='addLinkedIcon' />,
              onClick: () => handleCreateLinkedProfile(athlete.athleteId),
            },
          ]
        : [];
      const addDependentSubAccountItem = !athlete.primaryAccount
        ? [
            {
              title: 'Add Dependent Athlete',
              icon: <IconEdit size={theme.fontSizes.sm} data-cy='addDependentIcon' />,
              onClick: () => handleCreateDependentProfile(athlete.athleteId),
            },
          ]
        : [];

      const loginEmailItem =
        athleteRetrieve && !athleteRetrieve.isActive && handleSendLoginSetupEmail
          ? [
              {
                title: 'Send Login Setup Email',
                icon: <IconMail size={theme.fontSizes.sm} />,
                onClick: () => handleSendLoginSetupEmail(athleteRetrieve),
              },
            ]
          : [];

      // Hidden for dependents/linked (PM managed by primary); disabled when the
      // athlete already has a payment method on file; active otherwise.
      const requestPaymentInfoItem =
        athleteRetrieve && !athleteRetrieve.primaryAccount && handleRequestPaymentInfo
          ? [
              {
                title: 'Request payment info',
                icon: <IconSend size={theme.fontSizes.sm} />,
                disabled: athleteHasPaymentMethod,
                onClick: () => handleRequestPaymentInfo(athleteRetrieve),
              },
            ]
          : [];

      const loginToggleItem = athleteRetrieve
        ? [
            {
              title: athleteRetrieve.loginEnabled ? 'Disable Login' : 'Enable Login',
              icon: athleteRetrieve.loginEnabled ? (
                <IconCircleOff size={theme.fontSizes.sm} />
              ) : (
                <IconCheck size={theme.fontSizes.sm} />
              ),
              onClick: () => handleToggleLogin(),
            },
          ]
        : [];

      const notificationSettings = handleNotificationsSettings
        ? [
            {
              title: 'Notification Settings',
              icon: <IconMail size={theme.fontSizes.sm} data-cy='editAthleteButton' />,
              onClick: () => handleNotificationsSettings(),
            },
          ]
        : [];

      return [
        {
          title: 'Edit',
          icon: <IconEdit size={theme.fontSizes.sm} data-cy='editAthleteButton' />,
          onClick: () => handleEdit(athlete.athleteId),
        },
        ...activateWodupItem,
        ...reactivateWodupItem,
        ...expireWodupItem,
        ...addLinkedSubAccountItem,
        ...addDependentSubAccountItem,
        ...loginEmailItem,
        ...requestPaymentInfoItem,
        ...loginToggleItem,
        ...notificationSettings,
        {
          title: 'Delete',
          color: 'red',
          icon: <IconTrashX size={theme.fontSizes.sm} data-cy='deleteAthleteIcon' />,
          onClick: () => handleDelete(athlete.athleteId),
        },
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [athleteRetrieve, athleteList, theme.fontSizes.sm, wodupEnabled, athleteHasPaymentMethod],
  );

  return (
    <Group position='right' px='xs'>
      <Menu position='bottom-end' items={menuItems} target={target} disabled={disabled} />
    </Group>
  );
};
export default AthleteActionsMenu;
